import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMyGroups } from '../../hooks/useMyGroups';
import { usePublicGroups } from '../../hooks/usePublicGroups';
import { useGroupCounts } from '../../hooks/useGroupCounts';
import { useAdminPendingMembershipRequestsCountByGroup } from '../../hooks/useAdminPendingMembershipRequestsCountByGroup';
import './GroupsListPage.css';

function GroupsListPage({ user }) {
  const { groups, loading, error } = useMyGroups(user?.id);
  const { publicGroups, loading: publicLoading, error: publicError } = usePublicGroups(user?.id);
  const { countByGroupId: pendingRequestsByGroupId, loading: pendingRequestsLoading } =
    useAdminPendingMembershipRequestsCountByGroup(user?.id);
  const allGroupIds = useMemo(
    () => [...groups.map((g) => g.id), ...publicGroups.map((g) => g.id)],
    [groups, publicGroups]
  );
  const { memberCountByGroupId, thingCountByGroupId } = useGroupCounts(allGroupIds);

  return (
    <div className="groups-list-page">
      <div className="groups-list-header">
        <h2 className="tab-panel-title">My groups</h2>
        <Link to="/groups/new" className="submit-button groups-new-button">
          Create group
        </Link>
      </div>
      {loading && <p className="status">Loading your groups…</p>}
      {error && <p className="status error" role="alert">{error}</p>}
      {!loading && !error && groups.length === 0 && (
        <p className="status">You're not in any groups yet. Create one or browse public groups below.</p>
      )}
      {!loading && !error && groups.length > 0 && (
        <ul className="groups-list" aria-label="My groups">
          {groups.map((g) => (
            <li key={g.id}>
              <Link to={`/groups/${g.id}`} className="group-card group-card-link">
                <span className="group-name">
                  {g.name}
                  {g.myRole === 'ADMIN' && (
                    <span className="group-card-admin-badge" aria-label="You are an admin">Admin</span>
                  )}
                </span>
                {g.description && <span className="group-description">{g.description}</span>}
                <span className="group-card-summary">
                  {memberCountByGroupId[g.id] ?? 0} users sharing {thingCountByGroupId[g.id] ?? 0} items
                  {!pendingRequestsLoading && (pendingRequestsByGroupId[g.id] ?? 0) > 0 && (
                    <span className="group-card-pending">
                      {pendingRequestsByGroupId[g.id]} pending request
                      {pendingRequestsByGroupId[g.id] === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      <section className="groups-browse-section" aria-label="Browse public groups">
        <h3 className="groups-browse-title">Browse public groups</h3>
        {publicLoading && <p className="status">Loading public groups…</p>}
        {!publicLoading && publicError && <p className="status error">{publicError}</p>}
        {!publicLoading && !publicError && publicGroups.length === 0 && (
          <p className="status">No public groups right now.</p>
        )}
        {!publicLoading && !publicError && publicGroups.length > 0 && (
          <ul className="groups-list">
            {publicGroups.map((g) => (
              <li key={g.id}>
                <Link to={`/join/${g.invite_token}`} className="group-card group-card-link">
                  <span className="group-name">{g.name}</span>
                  {g.description && <span className="group-description">{g.description}</span>}
                  <span className="group-card-summary">
                    {memberCountByGroupId[g.id] ?? 0} users sharing {thingCountByGroupId[g.id] ?? 0} items
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

export default GroupsListPage;
